<template>
  <main class="l-main transition-[margin-right] duration-300 ease-in-out" :class="isSideOpen ? 'mr-[400px]' : 'mr-0'">
    <div class="bg-beige1">
      <Transition name="fade2">
        <div v-if="experienceFromCMS.id" class="md:pb-20">
          <MainVisualSectionCMS
            title-size-preset="experience"
            :images="mainVisualImgUrls"
            :is-sp="isSP"
            :pc-images="pcMainVisualImgUrls"
            :sp-images="spMainVisualImgUrls"
            :title-en="experience.name"
            hide-main-visual-button
          >
            <template #subtitle-content>
              <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-0 font-['Spectral'] text-[14px] font-normal leading-[24px] text-[#F4F1ED] md:hidden">
                <div v-for="item in experienceInfoItems" :key="item.icon" class="flex items-center gap-1">
                  <img class="h-4 w-4 brightness-0 invert md:h-6 md:w-6" :src="item.icon" />
                  <NuxtLink v-if="item.href" class="underline decoration-white/40 underline-offset-4" :to="item.href">{{ item.value }}</NuxtLink>
                  <span v-else>{{ item.value }}</span>
                </div>
              </div>
            </template>
          </MainVisualSectionCMS>
          <!-- .client.vueのSSRプレースホルダー（ハイドレーション時のレイアウトシフト防止） -->
          <ClientOnly>
            <MoleculesAnchorLinkMenu
              :active-switch-tab-index="activeDetailTab === 'story' ? TabIndex.TheStory : TabIndex.Gallery"
              :is-scrolling-up="isScrollingUp"
              :menu-texts="menuTexts"
              :on-switch-tab="onSwitchTab"
              :scroll-offset="scrollOffset"
              :switch-tab-indices="[TabIndex.TheStory, TabIndex.Gallery]"
              :target-refs="targetRefs"
            />
            <template #fallback>
              <div class="h-12 w-full border-b border-t border-gray1 bg-white"></div>
            </template>
          </ClientOnly>
          <div ref="overviewRef">
            <OrganismsPagesEnExperiencesDetailImageSection :img-urls="experienceDetailImageUrls" :lang="'en'" />
          </div>
          <!-- Gallery 表示中も Overview や Things to Know から開けるよう、ドロワーは The Story / Gallery の切り替えの外で Things to Know まで包む -->
          <TabbedDrawer :experience-cms="experienceFromCMS" :on-click-check-availability="onClickCheckAvailabilityDetail">
            <template #default="{ openDrawer, openDrawerAt }">
              <div ref="overviewSectionRef">
                <OrganismsPagesEnExperiencesDetailOverviewSection :experience="experience" :experience-cms="experienceFromCMS" :on-drawer-link-click="openDrawer" />
              </div>
              <!-- The Story / Gallery 切り替え -->
              <div ref="detailRef" class="relative overflow-hidden" :style="detailContainerStyle">
                <Transition :name="tabTransitionName" @after-enter="onAfterEnter" @before-leave="onBeforeLeave">
                  <OrganismsPagesEnExperiencesDetailFeatureDetailSection
                    v-if="activeDetailTab === 'story'"
                    key="story"
                    :experience-cms="experienceFromCMS"
                    :experience-sections="experienceSections"
                    :on-click-check-availability="onClickCheckAvailabilityDetail"
                    :on-drawer-link-click="openDrawer"
                  />
                  <GallerySection v-else key="gallery" :img-urls="experienceDetailImageUrls" :on-click-image="onClickGalleryImage" />
                </Transition>
              </div>
              <transition name="fade">
                <ExperienceDetailImageModal
                  v-if="isGalleryModalOpen"
                  :key="gallerySelectedIndex"
                  class="z-inpage-modal"
                  :img-urls="experienceDetailImageUrls"
                  :initial-index="gallerySelectedIndex"
                  :on-click-close="closeGalleryModal"
                />
              </transition>
              <div ref="locationRef">
                <OrganismsPagesEnExperiencesDetailLocationSection :experience="experience" />
              </div>
              <RequestForBookingSection
                ref="requestRef"
                :calendar-refresh-key="calendarRefreshKey"
                :experience="experience"
                :experience-id="slug"
                :is-waitlist-enabled="isWaitlistEnabled"
                :on-click-date="onClickDate"
                :on-click-get-notified="openWaitlistModal"
                :on-success="onSuccess"
              />
              <DatePickDrawer :external-open-state="isModalOpen" :on-click="noop" @update:is-modal-open="updateModalOpen">
                <Transition mode="out-in" name="fade4">
                  <ReservationStep1Form
                    v-if="drawerView === 'reservation'"
                    :calendar-refresh-key="calendarRefreshKey"
                    :experience="experience"
                    :experience-event="form.experienceEvent"
                    :experience-id="experience.slug"
                    :get-error-message="getErrorMessage"
                    :initial-update-from-experience-event="initialUpdateFromExperienceEvent"
                    :is-sending="isSending"
                    :is-skip-date-select="isSkipDateSelect"
                    :is-waitlist-enabled="isWaitlistEnabled"
                    :on-change-experience-event-id="onChangeExperienceEventId"
                    :on-click-get-notified="showWaitlistInDrawer"
                    :on-success="onReservationStep1Success"
                    :on-update-selected-date-and-exp-event-id="onUpdateSelectedDateAndExpEventId"
                    :pre-reservation-price="preReservationPrice"
                    :pre-reservation-price-with-guest-type="preReservationPriceWithGuestType"
                    :reservation-step1-form="form.step1"
                  />
                  <WaitlistRegistrationDrawerView v-else :experience-id="experience.id" :experience-name="experience.name" :experience-slug="experience.slug" :on-back="showReservationInDrawer" />
                </Transition>
              </DatePickDrawer>
              <WaitlistRegistrationModal
                v-if="isWaitlistEnabled"
                :experience-id="experience.id"
                :experience-name="experience.name"
                :experience-slug="experience.slug"
                :is-open="isWaitlistModalOpen"
                :on-close="closeWaitlistModal"
              />
              <div v-if="hasCustomerVoices" ref="customerVoiceRef">
                <OrganismsPagesEnExperiencesDetailCustomerVoiceSection :voices="experienceFromCMS.customerVoices!" />
              </div>
              <div v-if="hasThingsToKnow" ref="thingsToKnowRef">
                <ThingsToKnowSection :experience-cms="experienceFromCMS" :on-open-drawer-tab="openDrawerAt" />
              </div>
            </template>
          </TabbedDrawer>
          <OrganismsPagesEnExperiencesDetailForContactSection :experience-slug="slug" />
          <RecommendedExperiencesSection
            ref="recommendRef"
            :current-experience-slug="experience.slug"
            :experience-sections="experienceSections"
            :on-experience-click="handleExperienceClick"
            :on-view-all="handleViewAll"
            :popular-in-destination-experiences="popularInDestinationExperiences"
            :popular-in-destination-href="destinationHubPath"
            :popular-in-destination-name="experienceLocationNameRef"
            :related-experiences="relatedExperiences"
            :theme-list="themeList"
          />
          <!-- TODO :should-show=!isTargetDisplayingで適切に動作するようにする -->
          <ExperienceCTABar
            :experience="experience"
            :is-scrolling-down="isScrollingDown"
            :is-scrolling-up="isScrollingUp"
            :on-click-check-availability="onClickCheckAvailabilityCtaBar"
            :should-show="true"
          />
        </div>
      </Transition>
    </div>
  </main>
  <ClientOnly>
    <ChatFAB v-if="isAiChatEnabled" :has-overview-been-seen="hasOverviewBeenSeen" :is-cta-bar-visible="!isScrollingDown" :slug="slug" />
    <ChatPanel v-if="isAiChatEnabled" :on-click-booking-navigation="onClickCheckAvailabilityFromChat" :slug="slug" />
  </ClientOnly>
</template>

<script lang="ts" setup>
import { useIntersectionObserver } from '@vueuse/core';
import debounce from 'lodash/debounce';

import iconGroup from '@/assets/icon/group.svg';
import iconLocationOn from '@/assets/icon/wabunka/location_on.svg';
import iconSchedule from '@/assets/icon/wabunka/schedule.svg';
import iconWallet from '@/assets/icon/wabunka/wallet.svg';
import ExperienceDetailImageModal from '@/components/molecules/wabunka/ExperienceDetailImageModal/ExperienceDetailImageModal.vue';
import { useEnExperience } from '@/composables/models/useEnExperience';
import { useAddWabunkaExperienceBrowsingHistory } from '@/composables/models/wabunka/mutate/useAddWabunkaExperienceBrowsingHistory';
import { useAsyncWabunkaExperience } from '@/composables/models/wabunka/useAsyncWabunkaExperience';
import { useAsyncWabunkaExperiencesSearch } from '@/composables/models/wabunka/useAsyncWabunkaExperiencesSearch';
import { useGlobalModal } from '@/composables/ui/useGlobalModal';
import { useIsSP } from '@/composables/ui/useIsSP';
import { useBuildUrl } from '@/composables/utils/useBuildUrl';
import { useSafeWindow } from '@/composables/utils/useSafeWindow';
import { useErrorCreator } from '@/composables/utils/wabunka/useErrorCreator';
import { useScrollSensor } from '@/composables/utils/wabunka/useScrollSensor';
import { hasThingsToKnowSection } from '@/models/enExperience';
import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { stripHtmlTags } from '@/utils/stringUtils';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';
import { useFeatureFlag } from '@/wabunka-src/common/composables/general/useFeatureFlag/useFeatureFlag';
import { useLastModifiedHeader } from '@/wabunka-src/common/composables/general/useLastModifiedHeader/useLastModifiedHeader';
import RecommendedExperiencesSection from '@/wabunka-src/common/features/Experience/components/RecommendedExperiences/RecommendedExperiencesSection.vue';
import { useFeaturedListQuery } from '@/wabunka-src/common/features/Featured/composables/query/useGetFeaturedListQuery';
import MainVisualSectionCMS from '@/wabunka-src/common/features/MainVisual/components/MainVisualSectionCMS/MainVisualSectionCMS.vue';
import { useGlobalWabunkaReservationForm } from '@/wabunka-src/common/features/Reservation/composables/forms/useGlobalWabunkaReservationForm';
import { captureException } from '@/wabunka-src/common/utils';
import { sendAlgoliaExperienceViewedEvent } from '@/wabunka-src/common/utils/algolia/events/experienceViewed';
import { sendGAWabunkaCheckAvailabilityEvent, type WabunkaCheckAvailabilitySectionName } from '@/wabunka-src/common/utils/googleAnalytics/events/checkAvailability';
import { sendGAWabunkaSectionSlugViewEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/sectionSlugView';
import { sendGAWabunkaSectionViewAllEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/sectionViewAll';
import { EXPERIENCE_SECTION_DISPLAY_WEB_PAGE_PATHS } from '@/wabunka-src/constants/experienceSectionWebPagePath';
import { DETAIL_IMAGE_SEQUENCE_ID_THRESHOLD } from '@/wabunka-src/constants/experiences';
import { ICON_FONT_URL } from '@/wabunka-src/constants/iconFontUrl';
import ChatFAB from '@/wabunka-src/features/Chat/components/model/stateful/ChatFAB/ChatFAB.vue';
import ChatPanel from '@/wabunka-src/features/Chat/components/model/stateful/ChatPanel/ChatPanel.vue';
import { useChatPanel } from '@/wabunka-src/features/Chat/composables/general/useChatPanel';
import {
  convertToGuestTypeForm,
  useGetExperienceSectionsQuery,
  useWabunkaExperiencePreReservationPriceCalculationsForEvent,
  type ExperiencePreReservationPriceViewModel,
  type ExperiencePreReservationPriceWithGuestTypeViewModel,
  type ReservationQueryParams,
} from '@/wabunka-src/features/Experience';
import ExperienceCTABar from '@/wabunka-src/features/Experience/components/model/stateful/ExperienceCTABar/ExperienceCTABar.vue';
import { usePopularInDestinationExperiencesQuery } from '@/wabunka-src/features/Experience/composables/query/usePopularInDestinationExperiencesQuery';
import { useRelatedExperiencesQuery } from '@/wabunka-src/features/Experience/composables/query/useRelatedExperiencesQuery';
import { EXPERIENCE_RECOMMENDATION_LIMIT } from '@/wabunka-src/features/Experience/constants/recommendations';
import { displayPrice } from '@/wabunka-src/features/Experience/models/experience';
import { displayDestinationTagName } from '@/wabunka-src/features/Experience/models/wabunkaExperienceTag';
import { buildDestinationHubPath } from '@/wabunka-src/features/Experience/models/wabunkaExperienceTagHubLink';
import DatePickDrawer from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/pure/DatePickDrawer/DatePickDrawer.vue';
import GallerySection from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/pure/GallerySection/GallerySection.vue';
import RequestForBookingSection from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/RequestForBookingSection/RequestForBookingSection.vue';
import ReservationStep1Form from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/ReservationStep1Form/ReservationStep1Form.vue';
import TabbedDrawer from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/TabbedDrawer/TabbedDrawer.vue';
import ThingsToKnowSection from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/ThingsToKnowSection/ThingsToKnowSection.vue';
import WaitlistRegistrationDrawerView from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/WaitlistRegistrationDrawerView/WaitlistRegistrationDrawerView.vue';
import WaitlistRegistrationModal from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/WaitlistRegistrationModal/WaitlistRegistrationModal.vue';
import { useWabunkaExperiencePreReservationPriceCalculationsWithGuests } from '@/wabunka-src/features/Experience/subFeatures/Reservation/composable/query/useWabunkaExperiencePreReservationPriceCalculationsWithGuestType';

import type dayjs from 'dayjs';

/** タブのインデックス定義 */
const enum TabIndex {
  Overview = 0,
  TheStory = 1,
  Gallery = 2,
  Location = 3,
}

const isSP = useIsSP();

// メニューからのスクロール
const overviewRef = ref<HTMLElement | null>(null);
const detailRef = ref<HTMLElement | null>(null);
const locationRef = ref<HTMLElement | null>(null);
const requestRef = ref<HTMLElement | null>(null);
const customerVoiceRef = ref<HTMLElement | null>(null);
const thingsToKnowRef = ref<HTMLElement | null>(null);

// ChatFAB ツールチップ表示（overviewセクションを通過したらtrueにする）
const overviewSectionRef = ref<HTMLElement | null>(null);
const hasOverviewBeenSeen = ref(false);
useIntersectionObserver(overviewSectionRef, ([entry]) => {
  const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
  if (entry?.isIntersecting && scrollTop > 0) hasOverviewBeenSeen.value = true;
});
// stickyタブバーに隠れないようスクロール位置を調整（タブバー高さ48px + 余白）
const scrollOffset = computed(() => (isSP.value ? -75 : -45));

// The Story / Gallery 切り替え状態
const activeDetailTab = ref<'story' | 'gallery'>('story');
const tabTransitionName = ref<'slide-fade-left' | 'slide-fade-right'>('slide-fade-left');

// タブ切り替えハンドラ
const onSwitchTab = (index: number) => {
  if (index === TabIndex.TheStory) {
    tabTransitionName.value = 'slide-fade-right';
    activeDetailTab.value = 'story';
  } else if (index === TabIndex.Gallery) {
    tabTransitionName.value = 'slide-fade-left';
    activeDetailTab.value = 'gallery';
  }
};

// トランジション中のコンテナ高さ固定（チラつき防止）
const detailContainerHeight = ref<number | null>(null);
const detailContainerStyle = computed(() => {
  if (detailContainerHeight.value !== null) {
    return { minHeight: `${detailContainerHeight.value}px` };
  }
  return {};
});

const onBeforeLeave = () => {
  if (detailRef.value) {
    detailContainerHeight.value = detailRef.value.offsetHeight;
  }
};

const onAfterEnter = () => {
  detailContainerHeight.value = null;
};

const { window } = useSafeWindow();

const router = useRouter();
const route = useRoute();
const { isScrollingUp, isScrollingDown } = useScrollSensor(window);
// アンカーリンクメニューとCTABarの表示・非表示ロジックで使用したいが、対象コンポーネントのClientOnly化に伴い動作しなくなったので修正するまでコメントアウト
// const { isTargetDisplaying } = useDisplayChecker(recommendRef, '-80px'); // rootMargin:-80pxはRecommendedExperiencesSectionのpadding-top:80pxを除いた値
const { buildUrlWithEndSlash } = useBuildUrl();
const { createWabunkaError } = useErrorCreator();
// バックエンド から体験詳細情報を取得する
const slug = computed<string>(() => {
  const id = route.params.id;
  return Array.isArray(id) ? (id[0] ?? '') : id;
});

// MicroCMS から体験記事データを取得する
// TODO: MicroCMSから取得している体験記事データであることがわかるように、全体的に名称を変更する
const { enExperience: experienceFromCMS, hasError: hasEnError } = await useEnExperience(true);
useLastModifiedHeader(experienceFromCMS.value.revisedAt);
const { experience, isExperienceMissing } = await useAsyncWabunkaExperience(slug);
// 詳細画像セクションで表示する画像は、sequenceIdが6以上の画像
// sequenceId1~5はトップ・一覧ページで使用
const experienceDetailImageUrls = experience.value.images
  .filter((image) => {
    // 閾値以上のsequenceIdを持つ画像が存在するかチェック
    const hasImagesAfterThreshold = experience.value.images.some((img) => img.sequenceId >= DETAIL_IMAGE_SEQUENCE_ID_THRESHOLD);
    // 閾値以上の画像が存在する場合は、閾値以上の画像のみを表示
    // 存在しない場合は全ての画像を表示
    return hasImagesAfterThreshold ? image.sequenceId >= DETAIL_IMAGE_SEQUENCE_ID_THRESHOLD : true;
  })
  .map((image) => image.imageUrl);
const { experienceSections } = await useGetExperienceSectionsQuery(EXPERIENCE_SECTION_DISPLAY_WEB_PAGE_PATHS.EXPERIENCE_DETAIL, []);

const experienceIdRef = computed(() => experience.value.id);
const experienceLocationNameRef = computed(() => displayDestinationTagName(experience.value.tags));
const popularInDestinationDestinationWebPathRef = computed(() => experience.value.tags.find((t) => t.tag.tagGroupType === 'destination')?.tag.webPath ?? '');
const destinationHubPath = computed(() => buildDestinationHubPath(experience.value.tags));
// Popular in [Area] は destination 情報（エリア名・webPath）が揃って初めて成立する。
// どちらかが欠ける体験ではセクション自体を非表示にして、誤った文脈の補完が走らないようにする。
const hasPopularInDestinationContext = computed(() => Boolean(experienceLocationNameRef.value && popularInDestinationDestinationWebPathRef.value));
// destination 情報が無い体験ではセクション非表示なので、SSR 時の追加 fetch を空配列で短絡する
const emptyExperiences = async () => ({ experiences: ref<WabunkaExperienceViewModel[]>([]) });
// レコメンド本体: バックエンドが Algolia のランキング順で体験本体を返すため、フロントは並べ替え不要
const [{ experiences: relatedExperiencesRaw }, { experiences: popularInDestinationExperiencesRaw }, { experiences: popularInDestinationFallbackRaw }, { experiences: newestFallbackRaw }] =
  await Promise.all([
    useRelatedExperiencesQuery(experienceIdRef),
    hasPopularInDestinationContext.value ? usePopularInDestinationExperiencesQuery(experienceIdRef) : emptyExperiences(),
    // Popular in [Area] のフォールバック: Algolia の結果が不足したときに同じ destination の新着体験で埋める。
    // API は sort 未指定時に -createdAt（新着順）で返すため、明示指定は不要。
    hasPopularInDestinationContext.value
      ? useAsyncWabunkaExperiencesSearch({
          experienceEventStartAtDates: ref(''),
          destinations: popularInDestinationDestinationWebPathRef,
          themes: ref(''),
          slugs: ref([]),
          perPage: ref(EXPERIENCE_RECOMMENDATION_LIMIT + 1),
        })
      : emptyExperiences(),
    // You Might Also Like のフォールバック: Algolia の結果が不足したときに全体の新着体験で埋める。
    // API は sort 未指定時に -createdAt（新着順）で返すため、明示指定は不要。
    useAsyncWabunkaExperiencesSearch({
      experienceEventStartAtDates: ref(''),
      destinations: ref(''),
      themes: ref(''),
      slugs: ref([]),
      perPage: ref(EXPERIENCE_RECOMMENDATION_LIMIT + 1),
    }),
  ]);

// Algolia 結果が満たなければフォールバックで補完して先頭 EXPERIENCE_RECOMMENDATION_LIMIT 件に揃える。
// 補完時は Algolia 側の slug と表示中の体験 slug を除外して重複表示を防ぐ。
const fillWithFallback = (algoliaOrdered: WabunkaExperienceViewModel[], fallback: WabunkaExperienceViewModel[], currentSlug: string) => {
  if (algoliaOrdered.length >= EXPERIENCE_RECOMMENDATION_LIMIT) return algoliaOrdered;
  const usedSlugs = new Set([...algoliaOrdered.map((item) => item.slug), currentSlug]);
  const filled = fallback.filter((item) => !usedSlugs.has(item.slug));
  return [...algoliaOrdered, ...filled].slice(0, EXPERIENCE_RECOMMENDATION_LIMIT);
};

const relatedExperiences = computed(() => fillWithFallback(relatedExperiencesRaw.value, newestFallbackRaw.value ?? [], experience.value.slug));
const popularInDestinationExperiences = computed(() => {
  if (!hasPopularInDestinationContext.value) return [];
  return fillWithFallback(popularInDestinationExperiencesRaw.value, popularInDestinationFallbackRaw.value ?? [], experience.value.slug);
});

const { themeList } = await useFeaturedListQuery({ types: ['theme'] });
const { addExperienceBrowsingHistory } = useAddWabunkaExperienceBrowsingHistory();

// Review タブの表示条件
const hasCustomerVoices = computed(() => {
  return experienceFromCMS.value.customerVoices && experienceFromCMS.value.customerVoices.length > 0;
});

const hasThingsToKnow = computed(() => hasThingsToKnowSection(experienceFromCMS.value));

// タブ構成（Review は customerVoices がある場合のみ、Things To Know は一覧か FAQ がある場合のみ表示）
const menuTexts = computed(() => {
  const baseTexts = ['Overview', 'The Story', 'Gallery', 'Location'];
  const result = [...baseTexts];
  if (hasCustomerVoices.value) {
    result.push('Review');
  }
  if (hasThingsToKnow.value) {
    result.push('Things To Know');
  }
  return result;
});

// targetRefs（The Story と Gallery は detailRef を共有）
const targetRefs = computed(() => {
  const baseRefs = [overviewRef, detailRef, detailRef, locationRef];
  const result = [...baseRefs];
  if (hasCustomerVoices.value) {
    result.push(customerVoiceRef);
  }
  if (hasThingsToKnow.value) {
    result.push(thingsToKnowRef);
  }
  return result;
});

// Gallery モーダル
const gallerySelectedIndex = ref<number>(0);
const { isOpen: isGalleryModalOpen, open: openGalleryModal, close: closeGalleryModal } = useGlobalModal('experienceGalleryModal');
const onClickGalleryImage = (index: number) => {
  gallerySelectedIndex.value = index;
  openGalleryModal();
};

onMounted(() => {
  // ページ遷移時のちらつき防止のため即座にスクロールトップへ
  window.value?.scrollTo(0, 0);
  // 閲覧履歴を追加
  addExperienceBrowsingHistory(experience.value.slug);
  // Algolia Recommend 学習用に View イベントを送信
  sendAlgoliaExperienceViewedEvent(experience.value.id);
});

const {
  form,
  isSending,
  onChangeTotalPriceIncludingTax,
  onUpdateSelectedDateAndExpEventId,
  onClickStep1NextButton,
  getErrorMessage,
  initialUpdateFromExperienceEvent,
  reset,
  initialUpdateExperienceFromCMS,
  buildQueryParamsByForm,
  isSameExperienceEventAsQuery,
  onChangeExperienceEventId,
} = useGlobalWabunkaReservationForm();

// Unleashフラグを最上位で取得（リアクティブな値として保持）
const { featureFlag: isMultiPricingSwitchEnabled } = useFeatureFlag('wabunkaSwitchToMultiPricing');
const { featureFlag: isAiChatEnabled } = useFeatureFlag('wabunkaAiChat');
const { featureFlag: isWaitlistEnabled } = useFeatureFlag('wabunkaWaitlist');
const { isSideOpen, close: closeChatPanel } = useChatPanel();

// 体験詳細ページ離脱時にチャットパネルを閉じる（グローバル状態が他ページに残ってヘッダー幅などが崩れるのを防ぐ）
onBeforeUnmount(() => {
  closeChatPanel();
});
const runtimeConfig = useRuntimeConfig();

// クエリパラメータの削除処理
const shouldRemoveQueryParams = ref(false);
const currentQuery = route.query;

// クエリパラメータが存在する場合のみ削除処理を実行
if (Object.keys(currentQuery).length > 0) {
  shouldRemoveQueryParams.value = true;
}

// フォームの初期化完了を監視してクエリパラメータを削除
watch(
  [() => form.value.step1.isValidated, () => form.value.experienceEvent.id],
  ([isValidated, experienceEventId]) => {
    // フォームが初期化され、体験イベントIDが設定されている場合にクエリパラメータを削除
    if (shouldRemoveQueryParams.value && isValidated && experienceEventId) {
      router.replace({
        path: route.path,
        hash: route.hash,
      });
      shouldRemoveQueryParams.value = false; // 一度だけ実行するためのフラグをリセット
    }
  },
  { immediate: true },
);

const isModalOpen = ref(false);
const calendarRefreshKey = ref(0);
const isSkipDateSelect = ref(false);
const updateModalOpen = (newVal: boolean) => {
  isModalOpen.value = newVal;
};

// ウェイトリスト登録の表示制御
// 開いているドロワーの上にモーダルを重ねると、body ロックと重なり順の管理主体が二重になり、
// ドロワーの再マウントやロック解除漏れを招く。レイヤーを増やさないため、
// ページ直下の CTA はモーダル、ドロワー内の CTA はドロワーのビュー切り替えで出す
const isWaitlistModalOpen = ref(false);
const openWaitlistModal = () => {
  isWaitlistModalOpen.value = true;
};
const closeWaitlistModal = () => {
  isWaitlistModalOpen.value = false;
};
const drawerView = ref<'reservation' | 'waitlist'>('reservation');
const showWaitlistInDrawer = () => {
  drawerView.value = 'waitlist';
};
const showReservationInDrawer = () => {
  drawerView.value = 'reservation';
};
const onClickDate = (date: dayjs.Dayjs) => {
  // 選択した日付が変わった場合のみ、情報を更新
  if (!form.value.step1.selectedDate || !form.value.step1.selectedDate.isSame(date, 'day')) {
    form.value.step1.selectedDate = date;
    // 選択されたイベントおよびオプション情報をリセット
    form.value.step1.experienceEventId = '';
    form.value.step2.experienceEventInterpreterOption.id = '';
    form.value.step2.experienceEventOptions = [];
  }
  isModalOpen.value = true;
  // 時間選択モーダルを開く
  isSkipDateSelect.value = true;
};

if (hasEnError.value) {
  throw createWabunkaError({
    statusCode: 404,
    fatal: true,
  });
}

watch(
  () => experienceFromCMS.value,
  (experienceFromCMS) => {
    if (experienceFromCMS.id) {
      // 取得してきた experienceFromCMS.id が、form が保持している id と異なっていたら initialUpdate する
      if (experienceFromCMS.id !== '' && experienceFromCMS.id !== form.value.experienceFromCMS.id) {
        reset();
        initialUpdateExperienceFromCMS(experienceFromCMS);
      }
    }
  },
  { immediate: true },
);

watch(
  () => isModalOpen.value,
  (newValue) => {
    if (newValue) {
      // 前回ウェイトリスト画面のまま閉じていても、開き直したら予約フォームから始める
      drawerView.value = 'reservation';
      return;
    }
    // 据え置きのカレンダーとDrawer内のカレンダーの双方の変更分を反映させるためにrefreshKeyを更新
    calendarRefreshKey.value += 1;
  },
);
const query = computed<ReservationQueryParams>(() => route.query as unknown as ReservationQueryParams);

/**
 * フォーム送信成功時の処理
 */
const onSuccess = () => {
  // 旧予約エンドポイント(/reservations)を使用しようとしているかつUnleashフラグがONの場合
  // !isMultiPricePlanEnabled: これにより.envが反映されていないユーザーを対象にする
  // TODO: マルチプライシングリリース後一定期間が経ったらこのロジックを削除
  const isMultiPricePlanEnabled = runtimeConfig.public.IS_NEW_PRICE_PLAN_ENABLED;
  if (!isMultiPricePlanEnabled && isMultiPricingSwitchEnabled.value) {
    captureException(new Error(`user has been blocked because of multi pricing release`));
    // アラートを表示
    alert('The application has been updated. We apologize for the inconvenience, but please restart the reservation process from the beginning.');

    // 体験詳細ページへリダイレクト（強制リフレッシュ）
    if (process.client) {
      globalThis.window.location.href = `/experiences/${slug.value}/`;
    }
    return;
  }

  shouldRemoveQueryParams.value = false;

  const { expEvt, ppl, ops } = buildQueryParamsByForm();
  const params = new URLSearchParams({
    expEvt,
    ppl,
    ops: ops ?? '',
  } satisfies ReservationQueryParams);

  // 選択した体験イベントIDとクエリパラメータの体験イベントIDが一致する場合、オプションの値をクエリパラメータに含める
  if (isSameExperienceEventAsQuery(query.value)) {
    if (query.value.int) {
      params.set('int', query.value.int);
    }
  }

  router.push(`/experiences/${slug.value}/reserve/?${params}`);
};

// Check availabilityボタンをクリックした際の処理
const onClickCheckAvailability = (sectionName: WabunkaCheckAvailabilitySectionName) => {
  // 旧予約エンドポイント(/reservations)を使用しようとしているかつUnleashフラグがONの場合
  // !isMultiPricePlanEnabled: これにより.envが反映されていないユーザーを対象にする
  // TODO: マルチプライシングリリース後一定期間が経ったらこのロジックを削除
  const isMultiPricePlanEnabled = runtimeConfig.public.IS_NEW_PRICE_PLAN_ENABLED;
  if (!isMultiPricePlanEnabled && isMultiPricingSwitchEnabled.value) {
    captureException(new Error(`user has been blocked because of multi pricing release`));
    // アラートを表示
    alert('The application has been updated. We apologize for the inconvenience, but please restart the reservation process from the beginning.');

    // 体験詳細ページへリダイレクト（強制リフレッシュ）
    if (process.client) {
      globalThis.window.location.href = `/experiences/${slug.value}/`;
    }
    return;
  }

  isSkipDateSelect.value = false;
  isModalOpen.value = true;
  // GAイベント送信
  sendGAWabunkaCheckAvailabilityEvent(sectionName);
};
const onClickCheckAvailabilityDetail = () => onClickCheckAvailability('detail');
const onClickCheckAvailabilityCtaBar = () => onClickCheckAvailability('cta_bar');
const onClickCheckAvailabilityFromChat = () => onClickCheckAvailability('ai_chat');
const noop = () => {};

/** 体験カードがクリックされた際のイベントハンドラ */
const handleExperienceClick = (sectionId: string, experienceSlug: string) => {
  sendGAWabunkaSectionSlugViewEvent(sectionId, experienceSlug, route.name as string);
};

/** もっと見るボタンがクリックされた際のイベントハンドラ */
const handleViewAll = (sectionId: string) => {
  sendGAWabunkaSectionViewAllEvent(sectionId, route.name as string);
};
// メインビジュアルに表示する画像 url の配列を組み立てる
const mainVisualImgUrls = computed<string[]>(() => [isSP.value ? experienceFromCMS.value.spMainImage.url : experienceFromCMS.value.pcMainImage.url]);
const pcMainVisualImgUrls = computed<string[]>(() => [experienceFromCMS.value.pcMainImage.url]);
const spMainVisualImgUrls = computed<string[]>(() => [experienceFromCMS.value.spMainImage.url]);
const destinationTagName = computed(() => displayDestinationTagName(experience.value.tags));
const formattedPrice = computed(() => displayPrice(experience.value));
const experienceInfoItems = computed(() => {
  const items: { icon: string; value: string | undefined; href?: string }[] = [
    { icon: iconLocationOn, value: destinationTagName.value, href: destinationHubPath.value },
    { icon: iconWallet, value: formattedPrice.value },
    { icon: iconSchedule, value: experience.value.experienceDuration ? `${experience.value.experienceDuration.value}${experience.value.experienceDuration.unit}` : undefined },
    { icon: iconGroup, value: `Private: ${experience.value.minGuestsPerReservation}~${experience.value.maxGuests}` },
  ];
  // 値が未設定の項目はアイコンごと非表示にする
  return items.filter((item) => item.value !== undefined && item.value !== '');
});

// 以下メタ情報
// isExperienceMissing ガード: データ未取得（エッジキャッシュ通貨不一致等によるハイドレーション時の
// 一時的 null）を DRAFT と誤認して noindex を出さない（JCAT-5203）
const isNoIndex = computed(() => !isExperienceMissing.value && experience.value.publishStatus !== 'PUBLISHED');

const link = [{ rel: 'canonical' as const, href: buildUrlWithEndSlash(route.path) }];

definePageMeta({ layout: 'en-experience-id' });

const metaTitle = experienceFromCMS.value.metaTitle || experience.value.name;
// 関数側でも null/undefined/空文字をガード済み。引数型を string に戻した場合の保険として ?? '' を残す（多層防御 / JCAT-5180）
const metaDescription = experienceFromCMS.value.metaDescription || formatHtmlForDescriptionTag(experienceFromCMS.value.overviewV2 ?? '');

useHead({
  title: metaTitle,
  // getter にして isNoIndex の変化に追従させる。プレーン値だと setup 時点の評価で凍結され、
  // ハイドレーション時に一瞬データ欠落していた場合 canonical が永久に消える（JCAT-5203）
  link: () => (isNoIndex.value ? undefined : link),
});
useHead({
  link: [{ rel: 'stylesheet', href: ICON_FONT_URL }],
});

const ogImage = experience.value.images[0]?.imageUrl || `${runtimeConfig.public.BASE_URL}/en_ogp.png`;

useSeoMeta({
  title: metaTitle,
  description: metaDescription,
  ogTitle: metaTitle,
  ogDescription: metaDescription,
  ogImage,
  ogUrl: buildUrlWithEndSlash(route.path),
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterTitle: metaTitle,
  twitterDescription: metaDescription,
  twitterImage: ogImage,
});

// ヘッダーと meta を 1 呼び出しで同じ値にする（JCAT-6193）。index のときは呼ばない ——
// 値を渡すとサイト全体の indexable 判定と max-image-preview:large 等の既定を上書きしてしまう。
// 一度しか評価しないが JCAT-5203 は再発しない。useRobotsRule はクライアントでは
// meta を読むだけの ref を返して早期 return し、書き込まない（SSR で出した値がそのまま残る）。
if (isNoIndex.value) useRobotsRule('noindex, nofollow');

// Product 構造化データ（JSON-LD）

const productJsonLd = computed(() => {
  const base: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: experience.value.name,
    // 関数側でも null/undefined/空文字をガード済み。引数型を string に戻した場合の保険として ?? '' を残す（多層防御 / JCAT-5180）
    description: stripHtmlTags(experienceFromCMS.value.overviewV2 ?? ''),
    image: experience.value.images[0]?.imageUrl,
    brand: {
      '@type': 'Organization',
      name: 'Wabunka',
    },
  };

  if (experience.value.isPublicPrice) {
    base.offers = {
      '@type': 'Offer',
      price: experience.value.priceIncludingTax,
      priceCurrency: 'JPY',
      availability: 'https://schema.org/InStock',
      url: buildUrlWithEndSlash(route.path),
    };
  }

  const customerVoices = experienceFromCMS.value.customerVoices;
  if (customerVoices && customerVoices.length > 0) {
    base.review = customerVoices.map((voice) => ({
      '@type': 'Review',
      reviewBody: voice.content,
      author: {
        '@type': 'Person',
        name: voice.supplement || 'Anonymous',
      },
    }));
  }

  return JSON.stringify(base);
});

useHead({
  script: [{ type: 'application/ld+json', innerHTML: productJsonLd }],
});

// 料金計算用イベント関数
const { calculate } = useWabunkaExperiencePreReservationPriceCalculationsForEvent();

const { selectedCurrency } = useCurrency();

// 料金計算結果を保持する変数
const preReservationPrice = ref<ExperiencePreReservationPriceViewModel | undefined>();
const preReservationPriceWithGuestType = ref<ExperiencePreReservationPriceWithGuestTypeViewModel | undefined>();
const onReservationStep1Success = () => onClickStep1NextButton(onSuccess, preReservationPrice.value?.totalPriceIncludingTax ?? 0);

watch(
  [() => form.value.step1.experienceEventId, () => form.value.step1.numOfParticipants, () => form.value.step1.guestCounts, selectedCurrency],
  debounce(async () => {
    // 体験イベントが選択されていない場合は料金計算結果をリセット
    if (form.value.step1.experienceEventId == '') {
      preReservationPrice.value = undefined;
      preReservationPriceWithGuestType.value = undefined;
      return;
    }

    if (form.value.experienceEvent?.timeSlotId) {
      try {
        const guestTypeForm = convertToGuestTypeForm(form.value);
        const { calculate } = useWabunkaExperiencePreReservationPriceCalculationsWithGuests();
        const result = await calculate(ref(guestTypeForm), selectedCurrency.value);
        preReservationPriceWithGuestType.value = result.preReservationPrice.value;

        if (preReservationPriceWithGuestType.value) {
          onChangeTotalPriceIncludingTax(preReservationPriceWithGuestType.value.totalPriceIncludingTax);
        }
      } catch (error) {
        console.error('Failed to calculate price with guest type:', error);
        alert('Failed to calculate price. Please try selecting a different date/time or reload the page.');
      }
    } else if (form.value.experienceEvent?.id) {
      const { data, error } = await calculate(form, selectedCurrency.value);
      if (error || !data.value) {
        alert('Failed to calculate price. Please try selecting a different date/time or reload the page.');
        return;
      }
      preReservationPrice.value = data.value;
      if (data.value) {
        onChangeTotalPriceIncludingTax(data.value.totalPriceIncludingTax);
      }
    }
  }, 1000),
  { immediate: true },
);
</script>

<style scoped>
/* The Story / Gallery 切り替えトランジション共通 */
.slide-fade-left-enter-active,
.slide-fade-left-leave-active,
.slide-fade-right-enter-active,
.slide-fade-right-leave-active {
  transition: all 0.15s ease;
}

.slide-fade-left-leave-active,
.slide-fade-right-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

/* Left (Story → Gallery) */
.slide-fade-left-enter-from {
  opacity: 0;
  transform: translateX(20px);
}
.slide-fade-left-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Right (Gallery → Story) */
.slide-fade-right-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}
.slide-fade-right-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
</style>
